/**
 * What the week panel says when the works-order list is not there — and the distinction it
 * has to draw, because the two look identical and mean opposite things.
 *
 * A typed 422 is an ANSWER: the backend walked the bucket and there is nothing in it, so
 * "no operations to list" is a fact and the panel states it plainly. Anything else — a 5xx,
 * a dead dev proxy, a dropped connection — is the ABSENCE of an answer, and printing the
 * same sentence over it would assert something about the shop floor on the strength of a
 * failed HTTP request. That case gets an alert, a retry, and an explicit "missing, not
 * empty".
 *
 * The five figures above the list are unaffected either way: they come from the levelled
 * walk over a different mart, not from this drill. Saying so is what stops a failed list
 * reading as a broken panel.
 */

import React from 'react'
import type { ApiError } from '../../hooks/apiFetch'

export default function WeekDrillState({ error, paused, onRetry }: {
  error?: ApiError
  /** TanStack holding a retry back because the browser believes it is offline. */
  paused: boolean
  onRetry: () => void
}): React.JSX.Element {
  if (!error) {
    return (
      <p role="status" className="px-4 py-3 font-sans text-[11.5px]" style={{ color: 'var(--lc-mut)' }}>
        {paused
          ? 'Waiting for a connection to list the works orders behind this week.'
          : 'Loading the works orders behind this week…'}
      </p>
    )
  }
  const detail = (error.body?.detail as string | undefined) ?? error.message
  if (error.status === 422) {
    return (
      <p className="px-4 py-3 font-sans text-[11.5px]" style={{ color: 'var(--lc-ink3)' }}>
        No operations to list for this week — {detail}.
      </p>
    )
  }
  return (
    <div role="alert" className="flex flex-wrap items-center gap-3 px-4 py-3">
      <span className="font-sans text-[11.5px]" style={{ color: 'var(--lc-red-ink)' }}>
        The works orders behind this week could not be loaded — {detail}. The figures above come
        from the levelled walk and are unaffected; the list below is missing, not empty.
      </span>
      <button
        type="button"
        onClick={onRetry}
        className="rounded-md border px-2.5 py-1 font-sans text-[11px] font-semibold"
        style={{ borderColor: 'var(--lc-b12)', background: 'var(--lc-card)', color: 'var(--lc-ink3)' }}
      >
        Try again
      </button>
    </div>
  )
}
